import { useMemo, useState } from "react";
import { Slide, Grid, Container, Box } from "@mui/material";
import type { Practice } from "../../../@lib/types";
import {
  Card,
  ConditionalRenderer,
  DataDisplayMessage,
  Filters,
  Header,
  LatestReport,
  TabsFilters,
  useLanguageTranslator,
} from "../../../components";
import {
  usePracticesDocumentsCall,
  usePublicPracticesCall,
} from "../../../hooks";

export type PracticesCollectionData = {
  practices: Practice[];
};

function PublicPractices() {
  const [inputValue, setInputValue] = useState("");
  const [filters, setFilters] = useState({
    country: "",
    startYear: null,
    endYear: null,
    theme: "",
  });
  const [currTabIndex, setCurrTabIndex] = useState("all");

  const { translator } = useLanguageTranslator();

  const {
    practices = [],
    tabs,
    loading,
  } = usePublicPracticesCall({
    variables: {
      limit: 8,
      ...(filters?.country && filters?.country !== "0"
        ? { countryId: Number(filters?.country) }
        : {}),
      ...(filters?.theme && filters?.theme !== "0"
        ? { themeId: Number(filters?.theme) }
        : {}),
      ...(filters?.startYear ? { startYear: Number(filters?.startYear) } : {}),
      ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
    },
    currTabIndex: currTabIndex,
  });

  const { practiceDocuments, loading: practiceDocLoading } =
    usePracticesDocumentsCall({
      variables: {
        limit: 8,
        ...(filters?.country && filters?.country !== "0"
          ? { countryId: Number(filters?.country) }
          : {}),
        ...(filters?.theme && filters?.theme !== "0"
          ? { themeId: Number(filters?.theme) }
          : {}),
        ...(filters?.startYear
          ? { startYear: Number(filters?.startYear) }
          : {}),
        ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
      },
    }) as any;

  const filteredPracticesList = useMemo(() => {
    if (!inputValue) return practices || [];
    
    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return practices?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, practices]);

  const filteredPracticesDocumentsList = useMemo(() => {
    if (!inputValue) return practiceDocuments || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return practiceDocuments?.filter((item: any) =>
      item?.filename
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, practiceDocuments]);

  return (
    <Box pb={4}>
      <Header
        title={translator("Practices")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />
      <Filters setFilters={setFilters} filters={filters} />
      <Container sx={{ my: 3 }}>
        <TabsFilters
          currTab={currTabIndex}
          setCurrTab={setCurrTabIndex}
          listItems={tabs}
          heading={`${translator("Latest Practices")}`}
        />

        <Grid container spacing={2}>
          <ConditionalRenderer condition={practices?.length > 0}>
            {filteredPracticesList?.map((p: { id: number }, i: number) => (
              <Grid key={`${i}`} item xs={12} md={6} lg={3}>
                <Slide in timeout={180 * (i + 1)} direction="right">
                  <Card
                    {...p}
                    path={`/practices/${p?.id}`}
                    disabled={loading}
                    sx={{ height: "100%" }}
                    isPrivate={false}
                  />
                </Slide>
              </Grid>
            ))}
          </ConditionalRenderer>

          <DataDisplayMessage data={practices} loading={loading} />
        </Grid>
      </Container>

      <LatestReport
        items={filteredPracticesDocumentsList}
        loading={practiceDocLoading}
        path="/practices/view-all-Reports"
      />
    </Box>
  );
}

export { PublicPractices };
